import Container from '@mui/material/Container';
import { useColorScheme } from '@mui/material/styles';
import { createColorVariants } from 'src/components/muiTheme/color-schemes';

import { Box } from '../components/Box';
import { Typography } from '../components/Typography';

const ColorDisplay = ({ hex, title }) => {
  const colorRange = createColorVariants(hex);
  const { colorScheme } = useColorScheme();
  const colorTitle = title;

  const colorRangeForMode = colorRange[colorScheme || 'light'];

  const cellStyles = {
    height: '80px',
    width: '100%',
    p: 1,
    fontSize: '12px',
  };

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'var(--mui-palette-divider)',
        borderRadius: 1,
        overflow: 'hidden',
      }}
      my={4}
    >
      <Box height="38px" bgcolor={colorRangeForMode.og} p={1}>
        <Typography
          sx={{ fontSize: '14px' }}
          color={colorRangeForMode.contrastText}
          gutterBottom
        >
          Original color — {colorTitle} ({hex})
        </Typography>
      </Box>
      <Box mb={0} display="flex">
        <Box sx={cellStyles} color="black" bgcolor={colorRangeForMode[50]}>
          50
        </Box>
        <Box sx={cellStyles} color="black" bgcolor={colorRangeForMode[100]}>
          100
        </Box>
        <Box sx={cellStyles} color="black" bgcolor={colorRangeForMode[200]}>
          200
        </Box>
        <Box sx={cellStyles} color="black" bgcolor={colorRangeForMode[300]}>
          300
        </Box>
        <Box sx={cellStyles} color="black" bgcolor={colorRangeForMode[400]}>
          400
        </Box>
        <Box sx={cellStyles} color="white" bgcolor={colorRangeForMode[500]}>
          500
        </Box>
        <Box sx={cellStyles} color="white" bgcolor={colorRangeForMode[600]}>
          600
        </Box>
        <Box sx={cellStyles} color="white" bgcolor={colorRangeForMode[700]}>
          700
        </Box>
        <Box sx={cellStyles} color="white" bgcolor={colorRangeForMode[800]}>
          800
        </Box>
        <Box sx={cellStyles} color="white" bgcolor={colorRangeForMode[900]}>
          900
        </Box>
        <Box sx={cellStyles} color="white" bgcolor={colorRangeForMode[950]}>
          950
        </Box>
      </Box>
      <Box mb={0} display="flex">
        <Box sx={cellStyles} color="black" bgcolor={colorRangeForMode.lightest}>
          Lightest
        </Box>
        <Box sx={cellStyles} color="black" bgcolor={colorRangeForMode.light}>
          Light
        </Box>
        <Box sx={cellStyles} color="white" bgcolor={colorRangeForMode.main}>
          Main
        </Box>
        <Box sx={cellStyles} color="white" bgcolor={colorRangeForMode.dark}>
          Dark
        </Box>
        <Box sx={cellStyles} color="white" bgcolor={colorRangeForMode.darkest}>
          Darkest
        </Box>
      </Box>
      <Box height="38px" bgcolor={colorRangeForMode.main} p={1}>
        <Typography
          sx={{ fontSize: '14px' }}
          color={colorRangeForMode.contrastText}
          gutterBottom
        >
          Contrast Text
        </Typography>
      </Box>
    </Box>
  );
};

export default function Theme() {
  return (
    <Container>
      <ColorDisplay hex="#123456" title="Nile Blue" />
      <ColorDisplay hex="#20462b" title="Everglade" />
      <ColorDisplay hex="#aafdbc" title="Madang" />
      <ColorDisplay hex="#2d4367" title="Rhino" />
      <ColorDisplay hex="#70a7ff" title="Cornflower Blue" />
      <ColorDisplay hex="#d17a00" title="Meteor" />
      <ColorDisplay hex="#9a6f32" title="Buttered Rum" />
      <ColorDisplay hex="#ff70fa" title="Pink Flamingo" />
      <ColorDisplay hex="#635bff" title="Neon Blue" />
      <ColorDisplay hex="#333333" title="Mine Shaft" />
      <ColorDisplay hex="#666666" title="Steel" />
      <ColorDisplay hex="#999999" title="Aluminum" />
      <ColorDisplay hex="#CCCCCC" title="Iron" />
      <ColorDisplay hex="#FFFFFF" title="White" />
      <ColorDisplay hex="#000000" title="Black" />

      <ColorDisplay hex="#64748b" title="Slate" />
      <ColorDisplay hex="#6b7280" title="Gray" />
      <ColorDisplay hex="#71717a" title="Zinc" />
      <ColorDisplay hex="#737373" title="Neutral" />
      <ColorDisplay hex="#78716c" title="Stone" />
      <ColorDisplay hex="#ef4444" title="Red" />
      <ColorDisplay hex="#f97316" title="Orange" />
      <ColorDisplay hex="#f59e0b" title="Amber" />
      <ColorDisplay hex="#eab308" title="Yellow" />
      <ColorDisplay hex="#84cc16" title="Lime" />
      <ColorDisplay hex="#22c55e" title="Green" />
      <ColorDisplay hex="#10b981" title="Emerald" />
      <ColorDisplay hex="#14b8a6" title="Teal" />
      <ColorDisplay hex="#06b6d4" title="Cyan" />
      <ColorDisplay hex="#0ea5e9" title="Sky" />
      <ColorDisplay hex="#3b82f6" title="Blue" />
      <ColorDisplay hex="#6366f1" title="Indigo" />
      <ColorDisplay hex="#8b5cf6" title="Violet" />
      <ColorDisplay hex="#a855f7" title="Purple" />
      <ColorDisplay hex="#d946ef" title="Fushia" />
      <ColorDisplay hex="#ec4899" title="Pink" />
      <ColorDisplay hex="#f43f5e" title="Rose" />

      <ColorDisplay hex="#C1121F" title="Fire Engine Red" />
      <ColorDisplay hex="#F1FAEE" title="Feta" />
      <ColorDisplay hex="#A8DADC" title="Aqua Island" />
      <ColorDisplay hex="#457B9D" title="Wedgewood" />
      <ColorDisplay hex="#1D3557" title="Nile Blue" />

      <ColorDisplay hex="#606c38" title="Challet Green" />
      <ColorDisplay hex="#283618" title="Mallard" />
      <ColorDisplay hex="#fefae0" title="Solitaire" />
      <ColorDisplay hex="#dda15e" title="Porsche" />
      <ColorDisplay hex="#bc6c25" title="Bourbon" />

      <ColorDisplay hex="#F1ECF2" title="Athens Gray" />
      <ColorDisplay hex="#2E2731" title="Baltic Sea" />

      <ColorDisplay hex="#22092C" title="Jacaranda" />
      <ColorDisplay hex="#872341" title="Shiraz" />
      <ColorDisplay hex="#292425" title="Old Rose" />
      <ColorDisplay hex="#F05941" title="Carmine Pink" />

      <ColorDisplay hex="#1E0342" title="Telopea" />
      <ColorDisplay hex="#0E46A3" title="Cobalt" />
      <ColorDisplay hex="#9AC8CD" title="Morning Glory" />
      <ColorDisplay hex="#E1F7F5" title="White Ice" />

      <ColorDisplay hex="#0C0C0C" title="Cod Gray" />
      <ColorDisplay hex="#481E14" title="Paco" />
      <ColorDisplay hex="#9B3922" title="Prarie Sand" />
      <ColorDisplay hex="#F2613F" title="Outrageous Orange" />

      <ColorDisplay hex="#F5F7F8" title="Black Haze" />
      <ColorDisplay hex="#F4CE14" title="Ripe Lemon" />
      <ColorDisplay hex="#495E57" title="Veridian" />
      <ColorDisplay hex="#45474B" title="Steel Gray" />

      <ColorDisplay hex="#A8DF8E" title="Feijoa" />
      <ColorDisplay hex="#F3FDE8" title="Feta" />
      <ColorDisplay hex="#FFE5E5" title="Misty Rose" />
      <ColorDisplay hex="#FFBFBF" title="Your Pink" />

      <ColorDisplay hex="#22223b" title="Midnight Express" />
      <ColorDisplay hex="#4a4e69" title="East Bay" />
      <ColorDisplay hex="#9a8c98" title="Amythest Smoke" />
      <ColorDisplay hex="#c9ada7" title="Silk" />
      <ColorDisplay hex="#f2e9e4" title="Dawn Pink" />

      <ColorDisplay hex="#A75D5D" title="Au Chico" />
      <ColorDisplay hex="#D3756B" title="Contessa" />
      <ColorDisplay hex="#F0997D" title="Dark Salmon" />
      <ColorDisplay hex="#FFC3A1" title="Romantic" />

      <ColorDisplay hex="#472D2D" title="Woody Brown" />
      <ColorDisplay hex="#553939" title="Van Kleef" />
      <ColorDisplay hex="#704F4F" title="Ferra" />
      <ColorDisplay hex="#A77979" title="Pharlap" />
    </Container>
  );
}
